import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUploadGuard } from './UploadGuardContext';
import StepIndicator from './StepIndicator';
import { track } from './analytics';
import './UploadSteps.css';

const CATEGORY_OPTIONS = [
    { category: 'Exam / Consultation', canonicalService: 'Veterinary exam', preferredUnit: 'visit' },
    { category: 'Vaccination', canonicalService: 'Vaccine', preferredUnit: 'dose' },
    { category: 'Diagnostics', canonicalService: 'Diagnostic test', preferredUnit: 'test' },
    { category: 'Lab Work', canonicalService: 'Lab panel', preferredUnit: 'panel' },
    { category: 'Imaging', canonicalService: 'Imaging study', preferredUnit: 'study' },
    { category: 'Surgery', canonicalService: 'Surgical procedure', preferredUnit: 'procedure' },
    { category: 'Dental', canonicalService: 'Dental procedure', preferredUnit: 'procedure' },
    { category: 'Medication', canonicalService: 'Medication', preferredUnit: 'each' },
    { category: 'Anesthesia / Sedation', canonicalService: 'Anesthesia or sedation', preferredUnit: 'procedure' },
    { category: 'Hospitalization', canonicalService: 'Hospitalization', preferredUnit: 'day' },
    { category: 'Emergency / Urgent Care', canonicalService: 'Emergency care', preferredUnit: 'visit' },
    { category: 'Euthanasia / Aftercare', canonicalService: 'Euthanasia or aftercare', preferredUnit: 'procedure' },
    { category: 'Other', canonicalService: 'Other service', preferredUnit: 'each' }
];

const UNIT_OPTIONS = ['visit', 'dose', 'test', 'panel', 'study', 'procedure', 'tablet', 'capsule', 'ml', 'mg', 'day', 'hour', 'each'];

const PRICE_STATUS_OPTIONS = [
    { value: 'actual', label: 'Actual price paid' },
    { value: 'estimated_range', label: 'Estimate (low–high)' },
    { value: 'fixed_estimate', label: 'Estimate (single price)' },
    { value: 'unknown', label: 'Not sure' }
];

const CATEGORY_TAGS = {
    'Exam / Consultation': ['exam'],
    Vaccination: ['vaccines', 'preventive care'],
    Diagnostics: ['diagnostics'],
    'Lab Work': ['lab work', 'bloodwork'],
    Imaging: ['imaging'],
    Surgery: ['surgery'],
    Dental: ['dental'],
    Medication: ['medications'],
    'Anesthesia / Sedation': ['anesthesia'],
    Hospitalization: ['hospitalization'],
    'Emergency / Urgent Care': ['emergency'],
    'Euthanasia / Aftercare': ['aftercare']
};

const cleanMoney = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).replace(/[$,]/g, '');
};

const formatMoneyForReview = (value) => {
    const cleaned = cleanMoney(value);
    if (!cleaned) {
        return '';
    }

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : cleaned;
};

const MoneyField = ({ label, value, onChange }) => {
    return (
        <label className="service-money-field">
            <span className="field-label">{label}</span>
            <input
                type="text"
                inputMode="decimal"
                placeholder="Missing"
                value={value || ''}
                onChange={e => onChange(cleanMoney(e.target.value))}
                onBlur={() => onChange(formatMoneyForReview(value))}
            />
        </label>
    );
};

const hasAnyPrice = (service) => {
    return Boolean(cleanMoney(service.lowCost) || cleanMoney(service.highCost) || cleanMoney(service.totalCost));
};

// Catch the classic OCR/read mistake where a decimal point is misplaced or a
// digit is dropped (e.g. 2070 read instead of 20.70). If we have quantity, a
// unit price, and a total, quantity × unit price should land near the total.
// Returns { expected, total } when it looks off, otherwise null. Only applies
// to single-price rows — estimate ranges have no single total to reconcile.
const getPriceMismatch = (service) => {
    const priceStatus = service.normalized?.priceStatus || 'unknown';
    if (priceStatus === 'estimated_range') {
        return null;
    }

    const qty = Number(cleanMoney(service.quantity));
    const unit = Number(cleanMoney(service.unitCost));
    const total = Number(cleanMoney(service.totalCost));

    if (![qty, unit, total].every(Number.isFinite) || qty <= 0 || unit <= 0 || total <= 0) {
        return null;
    }

    const expected = qty * unit;
    // Tolerance: the larger of 2 cents or 2% of the expected amount, so normal
    // rounding and small tax/fee differences don't trip the warning.
    const tolerance = Math.max(0.02, expected * 0.02);
    if (Math.abs(expected - total) <= tolerance) {
        return null;
    }

    return { expected: expected.toFixed(2), total: total.toFixed(2) };
};

const getDocumentLabel = (documentType) => {
    return documentType ? String(documentType).toLowerCase() : 'document';
};

const normalizeDocumentType = (documentType) => {
    const value = String(documentType || '').trim().toLowerCase();

    if (value === 'estimate' || value === 'estimated' || value === 'treatment plan') {
        return 'estimate';
    }

    return 'bill';
};

const getVisibleMoneyFields = (priceStatus) => {
    return priceStatus === 'estimated_range' ? ['lowCost', 'highCost'] : ['totalCost'];
};

const createEditableService = (service = {}) => {
    const standardization = service.standardization || {};
    const initialCategory = standardization.category || service.category;
    const categoryOption = CATEGORY_OPTIONS.find(option => option.category === initialCategory) || CATEGORY_OPTIONS[CATEGORY_OPTIONS.length - 1];
    const normalized = service.normalized || {};
    const hasLowHighRange = cleanMoney(service.lowCost ?? normalized.lowEstimate) || cleanMoney(service.highCost ?? normalized.highEstimate);
    const defaultPriceStatus = normalized.priceStatus === 'estimated_range' && !hasLowHighRange
        ? 'fixed_estimate'
        : normalized.priceStatus || 'actual';

    return {
        ...service,
        description: service.description || '',
        quantity: service.quantity ?? normalized.quantity ?? 1,
        unitCost: formatMoneyForReview(service.unitCost ?? normalized.unitPrice),
        lowCost: formatMoneyForReview(service.lowCost ?? normalized.lowEstimate),
        highCost: formatMoneyForReview(service.highCost ?? normalized.highEstimate),
        totalCost: formatMoneyForReview(service.totalCost ?? normalized.total ?? normalized.actualCost),
        category: categoryOption.category,
        tags: Array.isArray(service.tags) ? service.tags : [],
        normalized: {
            ...normalized,
            unit: normalized.unit || categoryOption.preferredUnit,
            priceStatus: defaultPriceStatus
        },
        standardization: {
            ...standardization,
            category: standardization.category || categoryOption.category,
            canonicalService: standardization.canonicalService || categoryOption.canonicalService,
            preferredUnit: standardization.preferredUnit || categoryOption.preferredUnit,
            mappingStatus: standardization.mappingStatus || 'inferred',
            confidence: standardization.confidence ?? 0.5
        }
    };
};

const addUniqueTag = (tags, tag) => {
    const cleaned = String(tag || '').trim();
    if (!cleaned) {
        return tags;
    }

    const exists = tags.some(existingTag => existingTag.toLowerCase() === cleaned.toLowerCase());
    return exists ? tags : [...tags, cleaned];
};

const generateSuggestedTags = (data) => {
    let tags = Array.isArray(data.tags) ? [...data.tags] : [];
    const descriptionText = (data.services || []).map(service => service.description || '').join(' ').toLowerCase();

    tags = addUniqueTag(tags, (data.documentType || '').toLowerCase());

    (data.services || []).forEach(service => {
        const category = service.standardization?.category;
        (CATEGORY_TAGS[category] || []).forEach(tag => {
            tags = addUniqueTag(tags, tag);
        });

        (service.tags || []).forEach(tag => {
            tags = addUniqueTag(tags, tag);
        });
    });

    if (descriptionText.includes('feline') || descriptionText.includes('cat ')) {
        tags = addUniqueTag(tags, 'cat');
    }

    if (descriptionText.includes('canine') || descriptionText.includes('dog ')) {
        tags = addUniqueTag(tags, 'dog');
    }

    // Normalize to lowercase so tags read consistently (extraction-supplied
    // service tags arrive title-cased; the app's own tags are lowercase). The
    // list is already case-insensitively de-duplicated, so this can't collide.
    return tags.slice(0, 16).map(tag => String(tag).toLowerCase());
};

const Step2Review = ({ setOcrData }) => {
    const [reviewData, setReviewData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [activePreviewPage, setActivePreviewPage] = useState(0);
    const previewPageUrls = Array.isArray(reviewData?.pageUrls) && reviewData.pageUrls.length > 0
        ? reviewData.pageUrls
        : (reviewData?.uploadedFileUrl ? [reviewData.uploadedFileUrl] : []);
    const navigate = useNavigate();
    const location = useLocation();
    const ocrData = location.state?.ocrData;
    const { markUploadInProgress, clearUploadInProgress } = useUploadGuard();
    const documentLabel = getDocumentLabel(reviewData?.documentType || ocrData?.documentType);

    useEffect(() => {
        if (ocrData) {
            const editableData = {
                ...ocrData,
                vetHospital: ocrData.vetHospital || {},
                billDate: ocrData.billDate || '',
                documentType: normalizeDocumentType(ocrData.documentType),
                services: Array.isArray(ocrData.services) ? ocrData.services.map(createEditableService) : []
            };
            setReviewData(editableData);
            setOcrData(editableData);
            markUploadInProgress();
            track('review_viewed', { serviceCount: editableData.services.length });
        }
        setLoading(false);
    }, [ocrData, setOcrData, markUploadInProgress]);

    const reviewStats = useMemo(() => {
        const services = reviewData?.services || [];
        const needsReview = services.filter(service => (
            service.standardization?.mappingStatus === 'uncategorized' ||
            service.standardization?.unitMappingStatus === 'ambiguous' ||
            (service.standardization?.confidence || 0) < 0.6 ||
            !hasAnyPrice(service) ||
            Boolean(getPriceMismatch(service))
        )).length;

        return {
            total: services.length,
            needsReview
        };
    }, [reviewData]);

    const updateService = (index, field, value) => {
        setReviewData(prev => {
            const services = [...prev.services];
            services[index] = { ...services[index], [field]: value };
            return { ...prev, services };
        });
    };

    const updateServiceNested = (index, section, field, value) => {
        setReviewData(prev => {
            const services = [...prev.services];
            services[index] = {
                ...services[index],
                [section]: {
                    ...(services[index][section] || {}),
                    [field]: value
                }
            };
            return { ...prev, services };
        });
    };

    const updateCategory = (index, category) => {
        const selected = CATEGORY_OPTIONS.find(option => option.category === category) || CATEGORY_OPTIONS[CATEGORY_OPTIONS.length - 1];
        setReviewData(prev => {
            const services = [...prev.services];
            services[index] = {
                ...services[index],
                category: selected.category,
                normalized: {
                    ...(services[index].normalized || {}),
                    unit: services[index].normalized?.unit || selected.preferredUnit
                },
                standardization: {
                    ...(services[index].standardization || {}),
                    category: selected.category,
                    canonicalService: selected.canonicalService,
                    preferredUnit: selected.preferredUnit,
                    mappingStatus: 'matched',
                    confidence: 1
                }
            };
            return { ...prev, services };
        });
    };

    const removeService = (index) => {
        setReviewData(prev => ({
            ...prev,
            services: prev.services.filter((_, serviceIndex) => serviceIndex !== index)
        }));
    };

    const addService = () => {
        setReviewData(prev => ({
            ...prev,
            services: [
                ...prev.services,
                createEditableService({
                    description: '',
                    quantity: 1,
                    totalCost: '',
                    standardization: {
                        category: 'Other',
                        canonicalService: 'Other service',
                        preferredUnit: 'each',
                        mappingStatus: 'matched',
                        confidence: 1
                    },
                    normalized: {
                        unit: 'each',
                        priceStatus: prev.documentType?.toLowerCase() === 'estimate' ? 'estimated_range' : 'actual'
                    }
                })
            ]
        }));
    };

    const handleStartOver = () => {
        clearUploadInProgress();
        navigate('/upload/step1');
    };

    const handleHospitalChange = (field, value) => {
        setReviewData(prev => ({
            ...prev,
            vetHospital: {
                ...(prev.vetHospital || {}),
                [field]: value
            }
        }));
    };

    const handleNext = () => {
        const reviewedData = {
            ...reviewData,
            services: reviewData.services.map(service => ({
                ...service,
                standardization: {
                    ...(service.standardization || {}),
                    mappingStatus: service.standardization?.mappingStatus === 'uncategorized' ? 'matched' : service.standardization?.mappingStatus,
                    confidence: Math.max(service.standardization?.confidence || 0, 0.85)
                }
            }))
        };
        const suggestedTags = generateSuggestedTags(reviewedData);
        const reviewedDataWithTags = {
            ...reviewedData,
            tags: suggestedTags,
            suggestedTags
        };
        setOcrData(reviewedDataWithTags);
        navigate('/upload/step3', { state: { ocrData: reviewedDataWithTags, tags: suggestedTags } });
    };

    if (loading) {
        return <div className="upload-steps-container">Loading...</div>;
    }

    if (!reviewData) {
        return <div className="upload-steps-container">No extracted data found. Please upload the document again.</div>;
    }

    return (
        <div className="upload-steps-container review-container">
            <StepIndicator currentStep={2} />

            <div className="review-header">
                <div>
                    <h2>Check the details we found</h2>
                    <p>Fix anything that looks off. The most important things are the hospital, whether it’s a bill or an estimate, the service names, and the prices.</p>
                </div>
                <div className="review-stats">
                    <span>{reviewStats.total} service{reviewStats.total === 1 ? '' : 's'}</span>
                    <span>{reviewStats.needsReview} to double-check</span>
                </div>
            </div>
            <div className="review-layout">
                <aside className="document-preview">
                    <div className="document-preview-heading">
                        <strong>Your original {documentLabel}</strong>
                        <p>Compare what we found against your original {documentLabel}. You’ll hide any personal info in the next step.</p>
                    </div>
                    {previewPageUrls.length > 0 ? (
                        <>
                            <button type="button" className="document-preview-button" onClick={() => setPreviewOpen(true)}>
                                <img src={previewPageUrls[activePreviewPage]} alt={`Uploaded ${documentLabel} preview${previewPageUrls.length > 1 ? `, page ${activePreviewPage + 1}` : ''}`} />
                                <span>Open larger</span>
                            </button>
                            {previewPageUrls.length > 1 && (
                                <div className="page-pager">
                                    <button type="button" onClick={() => setActivePreviewPage(p => Math.max(0, p - 1))} disabled={activePreviewPage === 0} aria-label="Previous page">‹</button>
                                    <span>Page {activePreviewPage + 1} of {previewPageUrls.length}</span>
                                    <button type="button" onClick={() => setActivePreviewPage(p => Math.min(previewPageUrls.length - 1, p + 1))} disabled={activePreviewPage === previewPageUrls.length - 1} aria-label="Next page">›</button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="preview-empty">No image preview available</div>
                    )}
                </aside>

                <div className="review-main">
                    <section className="review-card">
                        <h3>Hospital &amp; document</h3>
                        <div className="review-form-grid">
                            <label>
                                Hospital name
                                <input value={reviewData.vetHospital?.name || ''} onChange={e => handleHospitalChange('name', e.target.value)} />
                            </label>
                            <label>
                                City
                                <input value={reviewData.vetHospital?.city || ''} onChange={e => handleHospitalChange('city', e.target.value)} />
                            </label>
                            <label>
                                State
                                <input value={reviewData.vetHospital?.state || ''} onChange={e => handleHospitalChange('state', e.target.value)} />
                            </label>
                            <label>
                                ZIP
                                <input value={reviewData.vetHospital?.zipCode || ''} onChange={e => handleHospitalChange('zipCode', e.target.value)} inputMode="numeric" />
                            </label>
                            <label>
                                Date
                                <input type="date" value={reviewData.billDate ? String(reviewData.billDate).slice(0, 10) : ''} onChange={e => setReviewData(prev => ({ ...prev, billDate: e.target.value }))} />
                            </label>
                            <label>
                                Type
                                <select value={reviewData.documentType || 'bill'} onChange={e => setReviewData(prev => ({ ...prev, documentType: e.target.value }))}>
                                    <option value="bill">Bill</option>
                                    <option value="estimate">Estimate</option>
                                </select>
                            </label>
                        </div>
                    </section>

                    <section className="review-card">
                        <div className="service-review-heading">
                            <h3>Services</h3>
                            <button type="button" onClick={addService}>Add service</button>
                        </div>
                        <div className="service-review-table-header">
                            <span>Service</span>
                            <span>Category</span>
                            <span>Unit</span>
                            <span>Qty</span>
                            {reviewData.documentType === 'estimate' ? (
                                <span className="price-header-range"><span>Low</span><span>High</span></span>
                            ) : (
                                <span>Price</span>
                            )}
                            <span>Price type</span>
                            <span></span>
                        </div>
                        <div className="service-review-list">
                            {reviewData.services.map((service, index) => {
                                const priceStatus = service.normalized?.priceStatus || 'unknown';
                                const visibleMoneyFields = getVisibleMoneyFields(priceStatus);
                                const priceMismatch = getPriceMismatch(service);

                                return (
                                    <div className={`service-review-row ${priceStatus === 'estimated_range' ? 'range-price-row' : 'single-price-row'} ${!hasAnyPrice(service) ? 'missing-price-row' : ''} ${priceMismatch ? 'price-mismatch-row' : ''}`} key={`${service.description}-${index}`}>
                                        <label className="service-description">
                                            <span className="field-label">Service description</span>
                                            <input value={service.description} onChange={e => updateService(index, 'description', e.target.value)} />
                                            {!hasAnyPrice(service) && <span className="missing-price-note">No price found — check your {documentLabel}</span>}
                                            {priceMismatch && (
                                                <span className="price-mismatch-note">
                                                    Double-check: {service.quantity} × ${service.unitCost} = ${priceMismatch.expected}, but the price says ${priceMismatch.total}
                                                </span>
                                            )}
                                        </label>
                                        <label className="service-category-field">
                                            <span className="field-label">Category</span>
                                            <select value={service.standardization?.category || 'Other'} onChange={e => updateCategory(index, e.target.value)}>
                                                {CATEGORY_OPTIONS.map(option => (
                                                    <option key={option.category} value={option.category}>{option.category}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className="service-unit-field">
                                            <span className="field-label">Unit</span>
                                            <select value={service.normalized?.unit || 'each'} onChange={e => updateServiceNested(index, 'normalized', 'unit', e.target.value)}>
                                                {UNIT_OPTIONS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
                                            </select>
                                        </label>
                                        <label className="service-qty-field">
                                            <span className="field-label">Qty</span>
                                            <input type="number" min="0" step="0.01" value={service.quantity} onChange={e => updateService(index, 'quantity', e.target.value)} />
                                        </label>
                                        <div className="service-price-fields">
                                            {visibleMoneyFields.includes('lowCost') && (
                                                <MoneyField label="Low" value={service.lowCost} onChange={value => updateService(index, 'lowCost', value)} />
                                            )}
                                            {visibleMoneyFields.includes('highCost') && (
                                                <MoneyField label="High" value={service.highCost} onChange={value => updateService(index, 'highCost', value)} />
                                            )}
                                            {visibleMoneyFields.includes('totalCost') && (
                                                <MoneyField label={priceStatus === 'estimated_range' ? 'Total' : 'Amount'} value={service.totalCost} onChange={value => updateService(index, 'totalCost', value)} />
                                            )}
                                        </div>
                                        <label className="service-price-field">
                                            <span className="field-label">Price type</span>
                                            <select value={priceStatus} onChange={e => updateServiceNested(index, 'normalized', 'priceStatus', e.target.value)}>
                                                {PRICE_STATUS_OPTIONS.map(option => (
                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                ))}
                                            </select>
                                        </label>
                                        <button type="button" className="remove-service-btn" onClick={() => removeService(index)}>Remove</button>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>
            </div>

            <div className="review-actions">
                <button onClick={handleStartOver} className="secondary-btn">Start over</button>
                <button onClick={handleNext} className="next-btn">Next: hide personal info</button>
            </div>

            {previewOpen && (
                <div className="document-lightbox" role="dialog" aria-modal="true" aria-label={`${documentLabel} image preview`} onClick={() => setPreviewOpen(false)}>
                    <button type="button" className="document-lightbox-close" onClick={() => setPreviewOpen(false)}>Close</button>
                    <img src={previewPageUrls[activePreviewPage]} alt={`Uploaded ${documentLabel} enlarged preview${previewPageUrls.length > 1 ? `, page ${activePreviewPage + 1}` : ''}`} onClick={e => e.stopPropagation()} />
                    {previewPageUrls.length > 1 && (
                        <div className="page-pager lightbox-pager" onClick={e => e.stopPropagation()}>
                            <button type="button" onClick={() => setActivePreviewPage(p => Math.max(0, p - 1))} disabled={activePreviewPage === 0} aria-label="Previous page">‹</button>
                            <span>Page {activePreviewPage + 1} of {previewPageUrls.length}</span>
                            <button type="button" onClick={() => setActivePreviewPage(p => Math.min(previewPageUrls.length - 1, p + 1))} disabled={activePreviewPage === previewPageUrls.length - 1} aria-label="Next page">›</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Step2Review;
